import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useGetList, useNotify } from 'react-admin';
import { Partner } from '../types';
import {
  PickupAvailability,
  PickupRequestPayload,
  ScheduledPickup,
  fetchPickupAvailability,
  modifyPickup,
  schedulePickup,
} from './canadaPostPickupApi';
import {
  formatDateLabel,
  minutesOf,
  nextBusinessDays,
  timeOptions,
} from './pickupSchedule';

interface SchedulePickupModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (pickup: ScheduledPickup) => void;
  /** Present when modifying an existing booking; absent when booking a new one. */
  pickup?: ScheduledPickup | null;
}

// Canada Post's driver comes in the afternoon; these are the windows they accept.
const READY_TIMES = timeOptions(12, 16);
const CLOSING_TIMES = timeOptions(13, 17);

const DEFAULT_READY = '13:00';
const DEFAULT_CLOSING = '16:30';

const formatAddress = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(', ');

export const SchedulePickupModal: React.FC<SchedulePickupModalProps> = ({
  open,
  onClose,
  onSaved,
  pickup = null,
}) => {
  const notify = useNotify();
  const isEdit = !!pickup;

  const [partnerId, setPartnerId] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [readyTime, setReadyTime] = useState(DEFAULT_READY);
  const [closingTime, setClosingTime] = useState(DEFAULT_CLOSING);
  const [volume, setVolume] = useState('1 box');
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [availability, setAvailability] = useState<PickupAvailability | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  // Pickup locations = the crematoria we hold the boxes at.
  const { data: crematoria, isLoading: loadingCrematoria } = useGetList<Partner>(
    'partners',
    {
      filter: { is_crematorium: true },
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: open && !isEdit },
  );

  const selectedPartner = (crematoria || []).find((partner) => partner.id === partnerId);
  const postalCode = isEdit ? pickup?.locationPostalCode : selectedPartner?.postal_code;
  const province = isEdit ? pickup?.locationProvince : selectedPartner?.province;

  // Reset to the booking being edited (or to a blank form) each time we open.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setAvailability(null);
    setAvailabilityError(null);
    if (pickup) {
      setPartnerId(pickup.partnerId || '');
      setPickupDate(pickup.pickupDate);
      setReadyTime(pickup.preferredTime);
      setClosingTime(pickup.closingTime);
      setVolume(pickup.pickupVolume || '1 box');
      setInstructions(pickup.pickupInstructions || '');
    } else {
      setPartnerId('');
      setPickupDate('');
      setReadyTime(DEFAULT_READY);
      setClosingTime(DEFAULT_CLOSING);
      setVolume('1 box');
      setInstructions('');
    }
  }, [open, pickup]);

  // Default to the only crematorium when there's just one — the dropdown is then
  // a confirmation rather than a decision.
  useEffect(() => {
    if (open && !isEdit && !partnerId && crematoria?.length === 1) {
      setPartnerId(String(crematoria[0].id));
    }
  }, [open, isEdit, partnerId, crematoria]);

  const loadAvailability = useCallback(async (postal: string) => {
    setCheckingAvailability(true);
    setAvailabilityError(null);
    // Drop the previous location's answer so the date strip can't offer days
    // Canada Post only offered at the crematorium we just navigated away from.
    setAvailability(null);
    try {
      setAvailability(await fetchPickupAvailability(postal));
    } catch (err) {
      setAvailability(null);
      setAvailabilityError(
        err instanceof Error ? err.message : 'Could not check pickup availability',
      );
    } finally {
      setCheckingAvailability(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !postalCode) return;
    void loadAvailability(postalCode);
  }, [open, postalCode, loadAvailability]);

  // Tomorrow onwards — same-day is not offered, so no cutoff applies here.
  const businessDays = nextBusinessDays(5, province);
  const noPickupHere =
    !!availability && !availability.onDemandTour && !availability.scheduledPickupsAvailable;

  const isDateSelectable = (date: string) => {
    // The day this pickup is already booked for stays selectable even if it has
    // since passed — otherwise "modify" would refuse to reopen its own booking.
    if (isEdit && date === pickup?.pickupDate) return true;
    if (!availability) return false;
    return !noPickupHere;
  };

  // Pre-select the first bookable day once we know Canada Post serves the address.
  useEffect(() => {
    if (!open || isEdit || !availability || pickupDate) return;
    const [firstOpen] = nextBusinessDays(5, province);
    if (firstOpen) setPickupDate(firstOpen);
  }, [open, isEdit, availability, pickupDate, province]);

  // Canada Post wants at least an hour between "ready" and "closing".
  const validClosingTimes = CLOSING_TIMES.filter(
    (time) => minutesOf(time) >= minutesOf(readyTime) + 60,
  );

  useEffect(() => {
    if (validClosingTimes.length && !validClosingTimes.includes(closingTime)) {
      setClosingTime(validClosingTimes[validClosingTimes.length - 1]);
    }
  }, [validClosingTimes, closingTime]);

  // The date shown in edit mode may already be in the past relative to the
  // five-day strip, so keep it as an option rather than silently dropping it.
  const dateChoices =
    isEdit && pickupDate && !businessDays.includes(pickupDate)
      ? [pickupDate, ...businessDays]
      : businessDays;

  const canSave =
    !saving &&
    !checkingAvailability &&
    !!pickupDate &&
    (isEdit || !!partnerId) &&
    isDateSelectable(pickupDate) &&
    validClosingTimes.length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: PickupRequestPayload = {
        pickupDate,
        preferredTime: readyTime,
        closingTime,
        pickupVolume: volume.trim() || undefined,
        pickupInstructions: instructions.trim() || undefined,
      };
      const saved = isEdit && pickup
        ? await modifyPickup(pickup.id, payload)
        : await schedulePickup({ ...payload, partnerId });
      notify(
        isEdit ? 'Pickup updated with Canada Post' : 'Pickup scheduled with Canada Post',
        { type: 'success' },
      );
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the pickup');
    } finally {
      setSaving(false);
    }
  };

  const locationLine = isEdit
    ? formatAddress([
        pickup?.locationAddress,
        pickup?.locationCity,
        pickup?.locationProvince,
        pickup?.locationPostalCode,
      ])
    : formatAddress([
        selectedPartner?.address,
        selectedPartner?.city,
        selectedPartner?.province,
        selectedPartner?.postal_code,
      ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>
        {isEdit ? 'Modify Canada Post Pickup' : 'Schedule Canada Post Pickup'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {(error || availabilityError) && (
            <Alert severity="error">{error || availabilityError}</Alert>
          )}

          {/* Location */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Pickup location
            </Typography>
            {isEdit ? (
              <>
                <Typography variant="body2" fontWeight="600">{pickup?.locationName}</Typography>
                <Typography variant="body2" color="text.secondary">{locationLine}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Canada Post can change the day and times of a booked pickup, but not where it
                  happens — cancel and re-book to move it.
                </Typography>
              </>
            ) : (
              <>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Crematorium"
                  value={partnerId}
                  onChange={(e) => {
                    setPartnerId(e.target.value);
                    setPickupDate('');
                  }}
                  disabled={loadingCrematoria}
                  helperText={
                    loadingCrematoria
                      ? 'Loading crematoria…'
                      : crematoria?.length
                        ? 'Where the driver collects the labelled boxes'
                        : 'No crematorium partners found'
                  }
                >
                  {(crematoria || []).map((partner) => (
                    <MenuItem key={partner.id} value={String(partner.id)}>
                      {partner.name}
                    </MenuItem>
                  ))}
                </TextField>
                {locationLine && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {locationLine}
                  </Typography>
                )}
                {selectedPartner && !selectedPartner.postal_code && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {selectedPartner.name} has no postal code on file — add it on the partner before
                    booking a pickup there.
                  </Alert>
                )}
              </>
            )}
          </Box>

          {/* Date */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Pickup day
            </Typography>
            {checkingAvailability ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Checking what Canada Post offers here…
                </Typography>
              </Stack>
            ) : !postalCode ? (
              <Typography variant="body2" color="text.secondary">
                Pick a crematorium to see the days Canada Post can come.
              </Typography>
            ) : (
              <>
                {noPickupHere && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    Canada Post offers no pickup at {postalCode} — the boxes have to be dropped off.
                  </Alert>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {dateChoices.map((date) => {
                    const selectable = isDateSelectable(date);
                    return (
                      <Chip
                        key={date}
                        label={formatDateLabel(date)}
                        color={pickupDate === date ? 'primary' : 'default'}
                        variant={pickupDate === date ? 'filled' : 'outlined'}
                        disabled={!selectable}
                        onClick={() => selectable && setPickupDate(date)}
                      />
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Pickups are booked from the next business day onwards; weekends and Canada Post
                  holidays are skipped.
                </Typography>
              </>
            )}
          </Box>

          {/* Times */}
          <Stack direction="row" spacing={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="Ready at"
              value={readyTime}
              onChange={(e) => setReadyTime(e.target.value)}
              helperText="Canada Post window: noon – 4:00 PM"
            >
              {READY_TIMES.map((time) => (
                <MenuItem key={time} value={time}>{time}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              label="Closes at"
              value={closingTime}
              onChange={(e) => setClosingTime(e.target.value)}
              helperText="At least 1h after ready"
            >
              {validClosingTimes.map((time) => (
                <MenuItem key={time} value={time}>{time}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            size="small"
            fullWidth
            label="Pickup volume"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            placeholder="e.g. 2 boxes"
          />

          <TextField
            size="small"
            fullWidth
            label="Pickup instructions (optional)"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="e.g. Ring the bell, side entrance"
            inputProps={{ maxLength: 40 }}
            helperText={`${instructions.length}/40 characters`}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving
            ? isEdit ? 'Updating…' : 'Scheduling…'
            : isEdit ? 'Update pickup' : 'Schedule pickup'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
